import React, { useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';

/**
 * Cuadro para dibujar la firma con el dedo o el mouse. Entrega un PNG (data URL) o null si está vacío.
 * La firma dibujada es un complemento: lo que da valor al contrato es el nombre tipeado, el código de verificación
 * y el registro (fecha, IP, sello) que guarda el servidor.
 */
export const SignaturePad: React.FC<{ onChange: (dataUrl: string | null) => void; height?: number }> = ({ onChange, height = 150 }) => {
    const ref = useRef<HTMLCanvasElement>(null);
    const drawing = useRef(false);
    const [empty, setEmpty] = useState(true);

    useEffect(() => {
        const c = ref.current;
        if (!c) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = c.clientWidth;
        c.width = w * dpr;
        c.height = height * dpr;
        const ctx = c.getContext('2d')!;
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#0f172a';
    }, [height]);

    const pos = (e: React.PointerEvent) => {
        const r = ref.current!.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const down = (e: React.PointerEvent) => {
        e.preventDefault();
        ref.current!.setPointerCapture(e.pointerId);
        drawing.current = true;
        const { x, y } = pos(e);
        const ctx = ref.current!.getContext('2d')!;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 0.01, y + 0.01);
        ctx.stroke();
    };
    const move = (e: React.PointerEvent) => {
        if (!drawing.current) return;
        const { x, y } = pos(e);
        const ctx = ref.current!.getContext('2d')!;
        ctx.lineTo(x, y);
        ctx.stroke();
    };
    const up = () => {
        if (!drawing.current) return;
        drawing.current = false;
        setEmpty(false);
        onChange(ref.current!.toDataURL('image/png'));
    };
    const clear = () => {
        const c = ref.current!;
        c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
        setEmpty(true);
        onChange(null);
    };

    return (
        <div>
            <div className="relative rounded-2xl border-2 border-dashed border-slate-300 bg-white overflow-hidden">
                <canvas ref={ref} style={{ width: '100%', height, touchAction: 'none', display: 'block', cursor: 'crosshair' }}
                    onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
                {empty && <p className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-slate-300 uppercase tracking-widest pointer-events-none">Firma aquí (opcional)</p>}
            </div>
            <button type="button" onClick={clear} className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-black text-slate-400 hover:text-slate-600 uppercase tracking-widest"><Eraser size={12} /> Borrar firma</button>
        </div>
    );
};

export default SignaturePad;
